import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  NotificationsActiveOutlined as ReminderIcon,
} from "@mui/icons-material";
import ResizablePanel from "../../components/common/ResizablePanel";
import { AddressbookPanelShell } from "../../components/component-contact";
import { contactCardSx } from "../../components/component-contact/ContactCard";
import { apiGetBlocks, apiCreateBlock, apiUpdateBlock, apiDeleteBlock } from "../../graphQL/blocks";
import { apiGetJournals, apiCreateJournal, apiDeleteJournal } from "../../graphQL/journal";

// Parent ToDo = block (schemaType "todo"). Each checklist entry = child block
// (schemaType "todoItem", parentId = the todo, category "open"/"done").
const SCHEMA_TODO = "todo";
const SCHEMA_ITEM = "todoItem";
const DRAFT_PREFIX = "draft-";

const ACTION_OPTIONS = [
  { value: "follow_up", label: "follow up" },
  { value: "call", label: "call" },
  { value: "email", label: "email" },
  { value: "text", label: "text" },
  { value: "schedule_meeting", label: "schedule a meeting" },
  { value: "meet_in_person", label: "meet in person" },
];

const addDuration = (baseDate, amount, unit) => {
  const d = new Date(baseDate);
  const n = Number(amount);
  if (!n || n <= 0) return d;
  if (unit === "day") d.setDate(d.getDate() + n);
  else if (unit === "week") d.setDate(d.getDate() + n * 7);
  else if (unit === "month") d.setMonth(d.getMonth() + n);
  else if (unit === "year") d.setFullYear(d.getFullYear() + n);
  return d;
};
const setTimeTo8am = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return d;
  d.setHours(8, 0, 0, 0);
  return d;
};

const fmtTime = (ts) => {
  const n = typeof ts === "number" ? ts : Number(ts) || Date.parse(ts);
  if (!n || Number.isNaN(n)) return "";
  return new Date(n).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// Human-readable date + time, e.g. "Jul 2, 2026 9:15pm".
const fmtDateTime = (ts) => {
  const n = typeof ts === "number" ? ts : Number(ts) || Date.parse(ts);
  if (!n || Number.isNaN(n)) return "";
  const d = new Date(n);
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12;
  if (h === 0) h = 12;
  return `${date} ${h}:${String(m).padStart(2, "0")}${ampm}`;
};

/* ─────────────────────────── List card ─────────────────────────── */

const TodoCard = ({ todo, selected, onSelect, onDelete }) => {
  const isDraft = todo._isNew;
  const done = todo._doneCount;
  const total = todo._totalCount;
  return (
    <Box sx={contactCardSx.card(selected, undefined, "#6366F1")} onClick={() => onSelect(todo)}>
      <Box sx={contactCardSx.cardHeader}>
        <Typography sx={contactCardSx.cardName}>{todo.name || (isDraft ? "New list" : "Untitled")}</Typography>
        {!isDraft && typeof total === "number" && total > 0 && (
          <Box component="span" sx={contactCardSx.statusChip(done === total ? "#DCFCE7" : "#EEF2FF", done === total ? "#166534" : "#3730A3")}>
            {done}/{total}
          </Box>
        )}
      </Box>
      {todo.desc ? (
        <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {todo.desc}
        </Typography>
      ) : null}
      <Box sx={contactCardSx.cardFooter}>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          {isDraft ? "Draft" : fmtTime(todo.updatedAt || todo.timestamp)}
        </Typography>
        <IconButton size="small" sx={contactCardSx.iconBtn} onClick={(e) => { e.stopPropagation(); onDelete(todo); }} aria-label="Delete list">
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

/* ─────────────────── New ToDo (inline) ─────────────────── */

const NewTodoForm = ({ onCreate, onCancel, saving }) => {
  const [title, setTitle] = useState("");
  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary", mb: 1 }}>New list</Typography>
      <TextField
        placeholder="List name"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && title.trim()) onCreate(title); }}
        fullWidth autoFocus variant="standard"
        InputProps={{ sx: { fontSize: 18, fontWeight: 700 } }}
        sx={{ mb: 2 }}
      />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={saving || !title.trim()} onClick={() => onCreate(title)}>
          {saving ? "Creating..." : "Create"}
        </Button>
      </Stack>
    </Box>
  );
};

/* ─────────────────── Checklist item row ─────────────────── */

const ChecklistItemRow = ({ item, index = 0, onToggle, onDelete, onSaveNote }) => {
  const checked = item.category === "done";
  const savedNote = item.data?.note || "";
  const completedAt = item.data?.completedAt;
  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState(savedNote);

  // Keep local draft in sync with persisted note when not actively editing.
  useEffect(() => { if (!editingNote) setNoteDraft(savedNote); }, [savedNote, editingNote]);

  const commitNote = () => {
    setEditingNote(false);
    const next = noteDraft.trim();
    if (next !== savedNote) onSaveNote(item, next);
  };

  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.5,
        backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F4F5FB",
        borderBottom: "1px solid #E8EAF6",
        "&:last-of-type": { borderBottom: "none" },
        transition: "background-color 120ms ease",
        "&:hover": { backgroundColor: "#E8EAF6" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Checkbox size="small" checked={checked} onChange={() => onToggle(item)} sx={{ p: 0.5 }} />
        <Typography sx={{ flex: 1, fontSize: 13, color: checked ? "#94A3B8" : "#1F2937", textDecoration: checked ? "line-through" : "none" }}>
          {item.name}
        </Typography>
        {item.timestamp ? (
          <Typography sx={{ fontSize: 11, color: "#9CA3AF", whiteSpace: "nowrap" }}>
            Added {fmtDateTime(item.timestamp)}
          </Typography>
        ) : null}
        {checked && completedAt ? (
          <Typography sx={{ fontSize: 11, color: "#3F51B5", fontWeight: 600, whiteSpace: "nowrap" }}>
            {fmtDateTime(completedAt)}
          </Typography>
        ) : null}
        <IconButton size="small" onClick={() => onDelete(item)} sx={{ color: "#94A3B8", "&:hover": { color: "#EF4444", backgroundColor: "#FEE2E2" } }} aria-label="Delete item">
          <DeleteIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
      <Box sx={{ ml: 4, mt: 0.25 }}>
        {editingNote ? (
          <TextField
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={commitNote}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitNote(); }
              if (e.key === "Escape") { setNoteDraft(savedNote); setEditingNote(false); }
            }}
            placeholder="Add a note..."
            autoFocus fullWidth multiline size="small" variant="standard"
            InputProps={{ sx: { fontSize: 12, color: "#475569" } }}
          />
        ) : savedNote ? (
          <Typography onClick={() => setEditingNote(true)}
            sx={{ fontSize: 12, color: "#475569", cursor: "text", whiteSpace: "pre-wrap", "&:hover": { color: "#3F51B5" } }}>
            {savedNote}
          </Typography>
        ) : (
          <Typography onClick={() => setEditingNote(true)}
            sx={{ fontSize: 12, color: "#B6B9C6", cursor: "text", fontStyle: "italic", "&:hover": { color: "#536DFE" } }}>
            Add a note...
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/* ─────────────────── Checklist ─────────────────── */

const Checklist = ({ todo, authorId, domain, onCountsChange, onItemsChange, initialItems }) => {
  // Seed from the page-level bulk fetch when available; only fall back to a
  // per-card refetch when the bulk fetch couldn't cover this list.
  const [items, setItems] = useState(() => (Array.isArray(initialItems) ? initialItems : []));
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGetBlocks({
        schemaType: SCHEMA_ITEM,
        parentId: todo.id,
        ordering: { field: "timestamp", order: "Asc" },
        pagination: { page: 1, limit: 200 },
      });
      const list = Array.isArray(res?.blocks) ? res.blocks : [];
      setItems(list);
      onCountsChange?.(todo.id, list.filter((i) => i.category === "done").length, list.length);
    } catch (err) {
      console.error("Failed to load checklist:", err);
    } finally {
      setLoading(false);
    }
  }, [todo.id, onCountsChange]);

  useEffect(() => {
    if (Array.isArray(initialItems)) return; // already seeded from the bulk fetch
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Keep the page-level cache in sync so re-opening this card reuses edits
  // without refetching.
  useEffect(() => { onItemsChange?.(todo.id, items); }, [items, todo.id, onItemsChange]);

  const addItem = async () => {
    const text = newText.trim();
    if (!text) return;
    try {
      const block = await apiCreateBlock({
        schemaType: SCHEMA_ITEM, type: "todoItem", parentId: todo.id,
        name: text, category: "open",
        ...(domain ? { domain } : {}), ...(authorId ? { authorId } : {}),
      });
      setNewText("");
      const next = block ? [...items, block] : items;
      setItems(next);
      onCountsChange?.(todo.id, next.filter((i) => i.category === "done").length, next.length);
      if (!block) load();
    } catch (err) { console.error("Failed to add item:", err); }
  };

  const toggle = async (item) => {
    const nextCat = item.category === "done" ? "open" : "done";
    // Backend replaces `data` wholesale on update, so build the full merged bag.
    const nextData = { ...(item.data || {}) };
    if (nextCat === "done") nextData.completedAt = new Date().toISOString();
    else delete nextData.completedAt;
    const optimistic = items.map((i) => (i.id === item.id ? { ...i, category: nextCat, data: nextData } : i));
    setItems(optimistic);
    onCountsChange?.(todo.id, optimistic.filter((i) => i.category === "done").length, optimistic.length);
    try {
      const updated = await apiUpdateBlock({ id: item.id, category: nextCat, data: nextData, updateNote: `checklist -> ${nextCat}` });
      if (updated) setItems((prev) => prev.map((i) => (i.id === item.id ? { ...updated } : i)));
    } catch (err) { console.error("Failed to toggle item:", err); load(); }
  };

  const removeItem = async (item) => {
    const next = items.filter((i) => i.id !== item.id);
    setItems(next);
    onCountsChange?.(todo.id, next.filter((i) => i.category === "done").length, next.length);
    try {
      await apiDeleteBlock(item.id);
    } catch (err) { console.error("Failed to delete item:", err); load(); }
  };

  const saveNote = async (item, note) => {
    // Merge into the freeform data bag (backend replaces data on update).
    const nextData = { ...(item.data || {}) };
    if (note) nextData.note = note;
    else delete nextData.note;
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, data: nextData } : i)));
    try {
      const updated = await apiUpdateBlock({ id: item.id, data: nextData, updateNote: "checklist note" });
      if (updated) setItems((prev) => prev.map((i) => (i.id === item.id ? { ...updated } : i)));
    } catch (err) { console.error("Failed to save note:", err); load(); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <TextField
          placeholder="Add checklist item..."
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
          fullWidth size="small" variant="standard"
        />
        <IconButton color="primary" onClick={addItem} disabled={!newText.trim()} aria-label="Add item"><AddIcon fontSize="small" /></IconButton>
      </Stack>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={18} /></Box>
      ) : items.length > 0 ? (
        <Box sx={{ mb: 1, borderRadius: 1, overflow: "hidden", border: "1px solid #E8EAF6" }}>
          {items.map((item, idx) => (
            <ChecklistItemRow key={item.id} item={item} index={idx} onToggle={toggle} onDelete={removeItem} onSaveNote={saveNote} />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

/* ─────────────────── Reminders (global, tied to this todo) ─────────────────── */

const TodoReminders = ({ todo, authorId }) => {
  const [reminders, setReminders] = useState([]);
  const [amount, setAmount] = useState(1);
  const [unit, setUnit] = useState("day");
  const [action, setAction] = useState("follow_up");
  const [saving, setSaving] = useState(false);

  // Active domain for silent journal isolation (never rendered).
  const reduxDomain = useSelector((state) => state.auth.user?.domain || "");
  const scopeDomain = reduxDomain && reduxDomain !== "All" ? reduxDomain : "";

  const load = useCallback(async () => {
    try {
      const res = await apiGetJournals({
        authorId, type: "reminder", sort: "dueDate", offset: 0, limit: 50,
        refCollection: "blocks", refId: todo.id,
        ...(scopeDomain ? { domain: scopeDomain } : {}),
      });
      setReminders(res?.journals || []);
    } catch (err) { console.error("Failed to load reminders:", err); }
  }, [authorId, todo.id, scopeDomain]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!authorId || saving) return;
    setSaving(true);
    try {
      const dueDate = setTimeTo8am(addDuration(new Date(), Number(amount) || 1, unit)).toISOString();
      await apiCreateJournal({
        authorId,
        ref: { collection: "blocks", id: todo.id },
        type: "reminder",
        dueDate,
        ...(scopeDomain ? { domain: scopeDomain } : {}),
        data: { amount: Number(amount) || 1, unit, action, text: todo.name || "", scope: "global", todoId: todo.id },
      });
      setAmount(1); setUnit("day"); setAction("follow_up");
      await load();
    } catch (err) { console.error("Failed to create reminder:", err); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    setReminders((prev) => prev.filter((x) => x.id !== r.id));
    try { await apiDeleteJournal(r.id); } catch (err) { console.error("Failed to delete reminder:", err); load(); }
  };

  const inlineSelectSx = { "& .MuiInputBase-root": { fontSize: 13, fontWeight: 600, color: "#312e81" } };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <ReminderIcon sx={{ fontSize: 16, color: "#D97706" }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Reminders</Typography>
      </Stack>

      {/* Sentence composer */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: reminders.length > 0 ? 1 : 0 }}>
        <Typography component="span" sx={{ fontSize: 13, color: "#475569" }}>Remind me in</Typography>
        <TextField
          type="number" variant="standard" value={amount}
          onChange={(e) => setAmount(Math.max(1, Number(e.target.value || 1)))}
          inputProps={{ min: 1, style: { textAlign: "center", width: 34, fontSize: 13, fontWeight: 700, color: "#312e81" } }}
          sx={{ mx: 0.25 }}
        />
        <TextField select variant="standard" value={unit} onChange={(e) => setUnit(e.target.value)} sx={inlineSelectSx}>
          <MenuItem value="day">{Number(amount) === 1 ? "day" : "days"}</MenuItem>
          <MenuItem value="week">{Number(amount) === 1 ? "week" : "weeks"}</MenuItem>
          <MenuItem value="month">{Number(amount) === 1 ? "month" : "months"}</MenuItem>
          <MenuItem value="year">{Number(amount) === 1 ? "year" : "years"}</MenuItem>
        </TextField>
        <Typography component="span" sx={{ fontSize: 13, color: "#475569" }}>to</Typography>
        <TextField select variant="standard" value={action} onChange={(e) => setAction(e.target.value)} sx={inlineSelectSx}>
          {ACTION_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
        </TextField>
        <Button size="small" variant="contained" onClick={create} disabled={saving} sx={{ ml: "auto", textTransform: "none" }}>
          {saving ? "..." : "Add"}
        </Button>
      </Box>

      {reminders.length > 0 && (
        <Stack spacing={0.5}>
          {reminders.map((r) => (
            <Box key={r.id} sx={{ display: "flex", alignItems: "center", gap: 0.5, p: 0.75, borderRadius: 1, backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" }}>
              <Typography sx={{ flex: 1, fontSize: 12, color: "#92400E" }}>
                {(ACTION_OPTIONS.find((a) => a.value === r.data?.action)?.label) || r.data?.action || "reminder"}
                {r.dueDate ? ` · ${fmtTime(r.dueDate)}` : ""}
              </Typography>
              <IconButton size="small" onClick={() => remove(r)} sx={{ color: "#B45309", "&:hover": { color: "#EF4444" } }} aria-label="Delete reminder">
                <DeleteIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

/* ─────────────────── Detail panel ─────────────────── */

const TodoDetailPanel = ({ todo, authorId, domain, onSave, onDelete, onCountsChange, onItemsChange, initialItems }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(todo.name || "");
  const [savingEdit, setSavingEdit] = useState(false);

  const commit = async () => {
    setSavingEdit(true);
    try { await onSave(todo, { name: title.trim() }); setEditing(false); }
    finally { setSavingEdit(false); }
  };

  return (
    <AddressbookPanelShell
      titleContent={
        editing ? (
          <TextField value={title} onChange={(e) => setTitle(e.target.value)} variant="standard" autoFocus fullWidth
            InputProps={{ sx: { fontSize: 14, fontWeight: 700 } }}
            onKeyDown={(e) => { if (e.key === "Enter" && title.trim()) commit(); }} />
        ) : (
          <Typography onDoubleClick={() => { setTitle(todo.name || ""); setEditing(true); }}
            sx={{ fontSize: 14, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
            {todo.name || "Untitled"}
          </Typography>
        )
      }
      headerActions={
        editing ? (
          <Button size="small" variant="contained" onClick={commit} disabled={savingEdit || !title.trim()} sx={{ textTransform: "none" }}>Save</Button>
        ) : (
          <Tooltip title="Delete list">
            <IconButton size="small" onClick={() => onDelete(todo)} sx={{ width: 24, height: 24, color: "#94A3B8", "&:hover": { color: "#EF4444", backgroundColor: "#FEE2E2" } }} aria-label="Delete list">
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )
      }
    >
      <TodoReminders todo={todo} authorId={authorId} />
      <Divider sx={{ my: 2 }} />
      <Checklist todo={todo} authorId={authorId} domain={domain} onCountsChange={onCountsChange} onItemsChange={onItemsChange} initialItems={initialItems} />
    </AddressbookPanelShell>
  );
};

/* ─────────────────── Page ─────────────────── */

const PageToDo = () => {
  const authorId = useSelector((state) => state.auth.userId);
  const user = useSelector((state) => state.auth.user);
  const userDomain = String(user?.domain || "").trim();
  const activeDomain = userDomain && userDomain !== "All" ? userDomain : "";

  const [loading, setLoading] = useState(false);
  const [todos, setTodos] = useState([]);
  // Checklist items grouped by parent todo, from the page-level bulk fetch;
  // opening a card reuses this instead of refetching per card.
  const [itemsByParent, setItemsByParent] = useState({});
  const [itemsBulkComplete, setItemsBulkComplete] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const notify = useCallback((message, severity = "success") => setToast({ open: true, message, severity }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const domainArg = activeDomain ? { domain: activeDomain } : {};
      // Load the lists and ALL checklist items together, so each card can show
      // its completed/total count without opening it.
      const [todosRes, itemsRes] = await Promise.all([
        apiGetBlocks({
          schemaType: SCHEMA_TODO, ...domainArg,
          ordering: { field: "updatedAt", order: "Desc" },
          pagination: { page: 1, limit: 200 },
        }),
        apiGetBlocks({
          schemaType: SCHEMA_ITEM, ...domainArg,
          pagination: { page: 1, limit: 2000 },
        }).catch(() => ({ blocks: [] })),
      ]);
      const list = (Array.isArray(todosRes?.blocks) ? todosRes.blocks : []).filter((b) => b.category !== "deleted");
      const itemBlocks = Array.isArray(itemsRes?.blocks) ? itemsRes.blocks : [];
      const counts = {};
      const byParent = {};
      for (const item of itemBlocks) {
        const p = item?.parentId;
        if (!p) continue;
        if (!counts[p]) counts[p] = { done: 0, total: 0 };
        counts[p].total += 1;
        if (item.category === "done") counts[p].done += 1;
        if (!byParent[p]) byParent[p] = [];
        byParent[p].push(item);
      }
      for (const key of Object.keys(byParent)) {
        byParent[key].sort((a, b) => (Number(a.timestamp) || 0) - (Number(b.timestamp) || 0));
      }
      setItemsByParent(byParent);
      // If the bulk fetch hit its limit, per-card fallback fetches stay enabled.
      setItemsBulkComplete(itemBlocks.length < 2000);
      setTodos(list.map((t) => ({ ...t, _doneCount: counts[t.id]?.done || 0, _totalCount: counts[t.id]?.total || 0 })));
    } catch (err) {
      console.error("Failed to load todos:", err);
      notify("Could not load ToDo lists", "error");
    } finally {
      setLoading(false);
    }
  }, [activeDomain, notify]);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => todos.find((t) => t.id === selectedId) || null, [todos, selectedId]);

  const setCounts = useCallback((todoId, done, total) => {
    setTodos((list) => list.map((t) => (t.id === todoId ? { ...t, _doneCount: done, _totalCount: total } : t)));
  }, []);

  const syncItems = useCallback((todoId, list) => {
    setItemsByParent((prev) => ({ ...prev, [todoId]: list }));
  }, []);

  const startNew = () => {
    if (todos.some((t) => t._isNew)) { setSelectedId(todos.find((t) => t._isNew).id); return; }
    const draft = { id: `${DRAFT_PREFIX}${Date.now()}`, _isNew: true, name: "", timestamp: Date.now() };
    setTodos((prev) => [draft, ...prev]);
    setSelectedId(draft.id);
  };
  const cancelDraft = () => { setTodos((prev) => prev.filter((t) => !t._isNew)); setSelectedId(null); };

  const createTodo = async (title) => {
    setSaving(true);
    try {
      const block = await apiCreateBlock({
        schemaType: SCHEMA_TODO, type: SCHEMA_TODO, name: title.trim(), category: "open",
        ...(activeDomain ? { domain: activeDomain } : {}), ...(authorId ? { authorId } : {}),
      });
      if (block) { setTodos((prev) => [block, ...prev.filter((t) => !t._isNew)]); setSelectedId(block.id); notify("List created"); }
      else { cancelDraft(); load(); }
    } catch (err) { console.error("Failed to create todo:", err); notify("Could not create list", "error"); }
    finally { setSaving(false); }
  };

  const saveTodo = async (todo, { name }) => {
    try {
      const updated = await apiUpdateBlock({ id: todo.id, name, updateNote: "renamed list" });
      const merged = updated || { ...todo, name };
      setTodos((list) => list.map((t) => (t.id === todo.id ? { ...merged, _doneCount: t._doneCount, _totalCount: t._totalCount } : t)));
      notify("List updated");
    } catch (err) { console.error("Failed to save todo:", err); notify("Could not save", "error"); }
  };

  const softDelete = async (todo) => {
    if (todo._isNew) { cancelDraft(); return; }
    try {
      await apiUpdateBlock({ id: todo.id, category: "deleted", updateNote: "deleted list" });
      setTodos((list) => list.filter((t) => t.id !== todo.id));
      if (selectedId === todo.id) setSelectedId(null);
      notify("List deleted");
    } catch (err) { console.error("Failed to delete:", err); notify("Could not delete", "error"); }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "row", height: "100%", minHeight: 0, minWidth: 0, overflow: "hidden", gap: 1 }}>
      {/* List panel */}
      <ResizablePanel edge="right" defaultWidth={360} minWidth={280} maxWidth={520}>
        <AddressbookPanelShell
          title="ToDo"
          headerActions={
            <IconButton size="small" onClick={startNew} title="New list"
              sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" }, width: 24, height: 24, p: 0 }}>
              <AddIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
            </IconButton>
          }
          footerLeft={`${todos.length} list${todos.length === 1 ? "" : "s"}`}
        >
          {loading ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}><CircularProgress size={20} /></Box>
          ) : todos.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography sx={{ color: "text.secondary", fontSize: 13 }}>No lists yet.</Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 11 }}>Use the + button to add one.</Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {todos.map((todo) => (
                <TodoCard key={todo.id} todo={todo} selected={todo.id === selectedId} onSelect={(t) => setSelectedId(t.id)} onDelete={softDelete} />
              ))}
            </Stack>
          )}
        </AddressbookPanelShell>
      </ResizablePanel>

      {/* Detail panel */}
      <ResizablePanel edge="right" defaultWidth={700} minWidth={300} maxWidth={700} fillRemaining>
        {selected && !selected._isNew ? (
          <TodoDetailPanel
            key={selected.id}
            todo={selected}
            authorId={authorId}
            domain={activeDomain}
            onSave={saveTodo}
            onDelete={softDelete}
            onCountsChange={setCounts}
            onItemsChange={syncItems}
            initialItems={itemsBulkComplete ? (itemsByParent[selected.id] || []) : itemsByParent[selected.id]}
          />
        ) : (
          <AddressbookPanelShell title={selected?._isNew ? "New list" : ""}>
            {selected?._isNew ? (
              <NewTodoForm onCreate={createTodo} onCancel={cancelDraft} saving={saving} />
            ) : (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography sx={{ color: "text.secondary", fontSize: 14 }}>Select a list to view its checklist</Typography>
              </Box>
            )}
          </AddressbookPanelShell>
        )}
      </ResizablePanel>

      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PageToDo;
